import { Link } from '@inertiajs/react'
import { BackofficeLayout } from '@/components/layout/BackofficeLayout'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Pagination, PaginationMeta } from '@/components/shared/Pagination'
import { BusinessRef } from '@/types'
import { Mail, ArrowRight, Plus } from 'lucide-react'

type AdminMailSummary = {
  id: string
  sender: string
  subject: string | null
  received_on: string | null
  published: boolean
  scanned: boolean
  shred_requested: boolean
  shredded: boolean
  created_at: string
}

type Props = {
  business: BusinessRef
  physical_mails: AdminMailSummary[]
  pagination: PaginationMeta
}

export default function BackofficeBusinessPhysicalMailsIndex({ business, physical_mails, pagination }: Props) {
  return (
    <BackofficeLayout
      title={`Physical Mail — ${business.name}`}
      breadcrumbs={[
        { label: 'Backoffice', href: '/backoffice/dashboard' },
        { label: 'Businesses', href: '/backoffice/businesses' },
        { label: business.name, href: `/backoffice/businesses/${business.id}` },
        { label: 'Physical Mail' },
      ]}
    >
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-muted-foreground" />
            <h1 className="text-xl font-semibold tracking-tight">Physical Mail</h1>
          </div>
          <Button asChild size="sm">
            <Link href={`/backoffice/businesses/${business.id}/physical_mails/new`}>
              <Plus className="w-3.5 h-3.5 mr-1.5" />
              Upload mail
            </Link>
          </Button>
        </div>

        <section className="border rounded-lg">
          {physical_mails.length > 0 ? (
            <div>
              {physical_mails.map((mail, i) => (
                <Link
                  key={mail.id}
                  href={`/backoffice/businesses/${business.id}/physical_mails/${mail.id}`}
                  className={`flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors ${i > 0 ? 'border-t' : ''}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-foreground truncate">{mail.sender}</div>
                    <div className="text-xs text-muted-foreground">
                      {mail.subject && <span>{mail.subject} · </span>}
                      {mail.received_on ? `Received: ${mail.received_on}` : new Date(mail.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 ml-3">
                    {mail.shredded ? (
                      <Badge className="bg-destructive text-destructive-foreground font-normal text-xs">Shredded</Badge>
                    ) : mail.shred_requested ? (
                      <Badge className="bg-orange-600 text-white font-normal text-xs">Shred requested</Badge>
                    ) : null}
                    {mail.scanned && (
                      <Badge className="bg-secondary text-secondary-foreground font-normal text-xs">Scanned</Badge>
                    )}
                    {mail.published ? (
                      <Badge className="bg-green-600 text-white font-normal text-xs">Published</Badge>
                    ) : (
                      <Badge className="bg-secondary text-secondary-foreground font-normal text-xs">Hidden</Badge>
                    )}
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="px-4 py-6 text-center text-sm text-muted-foreground">
              No physical mail yet
            </div>
          )}
        </section>

        <Pagination pagination={pagination} />
      </div>
    </BackofficeLayout>
  )
}
